import React, { useState, useEffect, useCallback, useRef } from 'react';
import PagesWarning from '@/components/documents/PagesWarning';
import SourceBadge from '@/components/documents/SourceBadge';
import { Inbox, Upload, CheckCircle, Clock, AlertTriangle, RefreshCw, FileText, Eye, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { documentAPI, accountingAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { BilagViewer } from '../../components/documents/BilagViewer';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../../lib/utils';

// Friendly labels derived from the SINGLE underlying document.status (no parallel state).
const INBOX_STATUS = {
    processing: { label: 'Processing…', variant: 'secondary', icon: Clock },
    review: { label: 'Received — with your accountant', variant: 'warning', icon: Clock },
    approved: { label: 'Booked', variant: 'success', icon: CheckCircle },
    rejected: { label: 'Needs attention — contact your accountant', variant: 'destructive', icon: AlertTriangle },
    unreadable: { label: "Couldn't read — please re-upload", variant: 'destructive', icon: AlertTriangle },
    ai_processing_failed: { label: "Couldn't read — please re-upload", variant: 'destructive', icon: AlertTriangle },
    filed: { label: 'Delivered to your accountant', variant: 'success', icon: CheckCircle },
};
const statusInfo = (s) => INBOX_STATUS[s] || { label: s || 'Unknown', variant: 'outline', icon: Clock };

// Allowed upload types depend on the chosen category. Bank statements accept CSV
// (for reconciliation import) OR PDF (delivered/printed) — and a CSV's file.type is
// often 'text/csv' or empty, so we also accept by extension.
const isFileAllowed = (file, category) => {
    const name = (file.name || '').toLowerCase();
    if (category === 'bank_statement') {
        return file.type === 'application/pdf' || name.endsWith('.pdf')
            || file.type === 'text/csv' || file.type === 'application/vnd.ms-excel' || name.endsWith('.csv');
    }
    return ['image/jpeg', 'image/png', 'application/pdf'].includes(file.type);
};
const UPLOAD_CONCURRENCY = 3;

// Keep each final file safely under e-conomic's ~9 MB downstream limit.
const MAX_UPLOAD_BYTES = 8.5 * 1024 * 1024;

const isHeic = (file) => {
    const n = (file.name || '').toLowerCase();
    return file.type === 'image/heic' || file.type === 'image/heif'
        || n.endsWith('.heic') || n.endsWith('.heif');
};

// Downscale + re-encode an image to JPEG until it's under maxBytes (handles big camera photos).
const compressImage = (file, maxBytes) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
        URL.revokeObjectURL(url);
        const w = img.naturalWidth, h = img.naturalHeight;
        const MAX_DIM = 2560;
        const baseScale = Math.min(1, MAX_DIM / Math.max(w, h));
        if (file.size <= maxBytes && baseScale === 1) { resolve(file); return; }   // already fine
        const render = (q, s) => new Promise((res) => {
            const c = document.createElement('canvas');
            c.width = Math.max(1, Math.round(w * s));
            c.height = Math.max(1, Math.round(h * s));
            c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
            c.toBlob((b) => res(b), 'image/jpeg', q);
        });
        let q = 0.85, s = baseScale, blob = await render(q, s), tries = 0;
        while (blob && blob.size > maxBytes && tries < 7) {
            if (q > 0.5) q -= 0.15; else s *= 0.8;   // drop quality first, then dimensions
            blob = await render(q, s); tries += 1;
        }
        if (!blob) { resolve(file); return; }
        const name = (file.name || 'photo').replace(/\.(png|jpe?g|heic|heif|webp)$/i, '') + '.jpg';
        resolve(new File([blob], name, { type: 'image/jpeg' }));
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
});

// Prepare a file for upload so what's selected always matches what the backend accepts:
// iPhone HEIC -> JPEG (lazy-loaded heic2any), then compress large photos under the limit.
// Bank statements (CSV/PDF) and ordinary PDFs pass through untouched.
const prepareFile = async (file, category) => {
    if (category === 'bank_statement') return file;
    let working = file;
    if (isHeic(file)) {
        const heic2any = (await import('heic2any')).default;
        const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
        const blob = Array.isArray(out) ? out[0] : out;
        const name = (file.name || 'photo').replace(/\.(heic|heif)$/i, '') + '.jpg';
        working = new File([blob], name, { type: 'image/jpeg' });
    }
    if (working.type && working.type.startsWith('image/')) {
        working = await compressImage(working, MAX_UPLOAD_BYTES);
    }
    return working;
};

// Configurable client-upload categories (the "I'm sending" set). Fetched from the backend;
// this is the offline fallback. Each maps to a doc_category + a document_type HINT.
const DEFAULT_INTAKE = [
    { key: 'expense_receipt', label: 'Expense receipt', doc_category: 'invoice', document_type_hint: 'purchase' },
    { key: 'income_settlement', label: 'Income settlement', doc_category: 'invoice', document_type_hint: 'sale' },
    { key: 'bank_statement', label: 'Bank statement', doc_category: 'bank_statement', document_type_hint: 'bank_statement' },
    { key: 'other', label: 'Other document', doc_category: 'other', document_type_hint: null },
];

export default function InboxPage() {
    const { user } = useAuth();
    const { currentTenant } = useTenant();
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadQueue, setUploadQueue] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const [intakeKey, setIntakeKey] = useState('expense_receipt');
    const [categories, setCategories] = useState(DEFAULT_INTAKE);
    const intakeCat = categories.find((c) => c.key === intakeKey) || categories[0] || {};
    const uploadCategory = intakeCat.doc_category || 'invoice';        // doc-routing (existing logic)
    const docTypeHint = intakeCat.document_type_hint || null;          // classification HINT
    const [viewDoc, setViewDoc] = useState(null);
    const [docToDelete, setDocToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const pollRef = useRef(null);

    const handleDeleteDoc = async () => {
        if (!docToDelete) return;
        setDeleting(true);
        try {
            await documentAPI.deleteDocument(docToDelete.id);
            toast.success('Document deleted');
            setDocToDelete(null);
            fetchDocs();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete document');
        } finally {
            setDeleting(false);
        }
    };

    const fetchDocs = useCallback(async () => {
        if (!currentTenant) { setLoading(false); return; }
        try {
            const res = await documentAPI.getAll(currentTenant.id);
            setDocs(res.data || []);
        } catch (err) {
            console.error('Failed to load inbox:', err);
        } finally {
            setLoading(false);
        }
    }, [currentTenant]);

    useEffect(() => { fetchDocs(); }, [fetchDocs]);
    useEffect(() => {
        accountingAPI.getClassification()
            .then((r) => { if (r.data?.intake_categories?.length) setCategories(r.data.intake_categories); })
            .catch(() => {});
    }, []);

    // Auto-refresh while anything is still processing, so status updates appear.
    useEffect(() => {
        const anyPending = docs.some((d) => d.status === 'processing');
        if (anyPending && !pollRef.current) {
            pollRef.current = setInterval(fetchDocs, 8000);
        } else if (!anyPending && pollRef.current) {
            clearInterval(pollRef.current); pollRef.current = null;
        }
        return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    }, [docs, fetchDocs]);

    const handleFilesUpload = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        if (!currentTenant) { toast.error('No company linked to your account yet — contact your accountant.'); return; }

        setUploading(true);
        // Show the queue immediately; HEIC/large photos are converted + compressed first.
        setUploadQueue(files.map((f) => ({ name: f.name, status: 'preparing', error: null })));

        const setItemStatus = (idx, status, error = null) =>
            setUploadQueue((prev) => prev.map((it, i) => (i === idx ? { ...it, status, error } : it)));

        // Prepare each file (HEIC -> JPEG + compress), then validate the RESULT — so a
        // selectable file is never rejected by the backend afterwards.
        const items = [];
        for (let i = 0; i < files.length; i++) {
            try {
                const file = await prepareFile(files[i], uploadCategory);
                const valid = isFileAllowed(file, uploadCategory);
                items.push({ file, valid });
                setItemStatus(i, valid ? 'queued' : 'failed',
                    valid ? null : (uploadCategory === 'bank_statement' ? 'Bank statement must be CSV or PDF' : 'Unsupported file type'));
            } catch (err) {
                items.push({ file: files[i], valid: false });
                setItemStatus(i, 'failed', 'Could not read this photo — try a different file');
            }
        }

        const pending = items.map((it, idx) => idx).filter((idx) => items[idx].valid);
        let cursor = 0, succeeded = 0, failed = items.length - pending.length;

        const worker = async () => {
            while (cursor < pending.length) {
                const idx = pending[cursor++];
                setItemStatus(idx, 'uploading');
                try {
                    await documentAPI.upload(items[idx].file, currentTenant.id, uploadCategory, docTypeHint);
                    setItemStatus(idx, 'done'); succeeded += 1;
                } catch (err) {
                    setItemStatus(idx, 'failed', err.response?.data?.detail || 'Upload failed'); failed += 1;
                }
            }
        };
        await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, pending.length) }, () => worker()));

        setUploading(false);
        fetchDocs();
        if (failed === 0) toast.success(`Uploaded ${succeeded} file${succeeded === 1 ? '' : 's'}. Your accountant will receive them.`);
        else if (succeeded === 0) toast.error(`Could not upload ${failed} file${failed === 1 ? '' : 's'}.`);
        else toast.warning(`Uploaded ${succeeded} of ${files.length}. ${failed} failed — see the list.`);
    };

    const onDrop = (e) => {
        e.preventDefault(); setDragActive(false);
        if (!uploading && e.dataTransfer?.files?.length) handleFilesUpload(e.dataTransfer.files);
    };

    return (
        <div className="space-y-6" data-testid="inbox-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
                        <Inbox className="h-7 w-7 text-primary" /> Your bilag inbox
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {user?.name ? `${user.name} — ` : ''}{currentTenant?.name || 'your company'}.
                        Upload receipts and invoices; your accountant receives them automatically.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchDocs} data-testid="inbox-refresh">
                    <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
            </div>

            {/* Upload zone */}
            <input
                type="file" id="inbox-upload" className="hidden"
                accept={uploadCategory === 'bank_statement' ? '.csv,.pdf,text/csv,application/pdf' : 'image/*,application/pdf'}
                multiple={uploadCategory !== 'bank_statement'}
                onChange={(e) => { handleFilesUpload(e.target.files); e.target.value = ''; }}
                data-testid="inbox-upload-input"
            />
            <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground whitespace-nowrap">I'm sending:</span>
                <Select value={intakeKey} onValueChange={setIntakeKey} disabled={uploading}>
                    <SelectTrigger className="w-56" data-testid="inbox-upload-type">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {categories.map((c) => (<SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>))}
                    </SelectContent>
                </Select>
            </div>
            <div
                onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragActive(true); }}
                onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                onDrop={onDrop}
                onClick={() => { if (!uploading) document.getElementById('inbox-upload')?.click(); }}
                className={`rounded-lg border-2 border-dashed p-6 sm:p-8 text-center transition-colors ${dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/50'} ${uploading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                data-testid="inbox-dropzone"
            >
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="font-medium">
                    {/* Touch-friendly copy on mobile; drag & drop only mentioned on larger screens */}
                    <span className="sm:hidden">
                        {uploadCategory === 'bank_statement'
                            ? 'Tap to choose your bank statement'
                            : `Tap to add your ${uploadCategory === 'other' ? 'document' : 'bilag'} — Camera, Photos or Files`}
                    </span>
                    <span className="hidden sm:inline">
                        Drag &amp; drop your {uploadCategory === 'bank_statement' ? 'bank statement' : uploadCategory === 'other' ? 'document' : 'bilag'} here, or click to choose files
                    </span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                    {uploadCategory === 'bank_statement'
                        ? 'CSV (for reconciliation) or PDF (for filing) · up to 10 MB · delivered to your accountant (not auto-imported)'
                        : 'PDF, JPG or PNG · up to 10 MB each · multiple files OK'}
                </p>
            </div>

            {/* Per-file upload progress */}
            {uploadQueue.length > 0 && (
                <Card>
                    <CardContent className="p-4 space-y-1">
                        {uploadQueue.map((f, i) => (
                            <div key={i} className="flex items-center justify-between text-sm py-1">
                                <span className="flex items-center gap-2 min-w-0">
                                    {f.status === 'queued' && <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                                    {(f.status === 'uploading' || f.status === 'preparing') && <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin shrink-0" />}
                                    {f.status === 'done' && <CheckCircle className="h-3.5 w-3.5 text-success shrink-0" />}
                                    {f.status === 'failed' && <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />}
                                    <span className="truncate">{f.name}</span>
                                </span>
                                <span className={`text-xs ${f.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'}`}>
                                    {f.status === 'failed' ? (f.error || 'failed') : f.status}
                                </span>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* Uploads list */}
            <Card>
                <CardHeader><CardTitle>My uploads ({docs.length})</CardTitle></CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : docs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <FileText className="h-10 w-10 text-muted-foreground mb-3" />
                            <p className="text-muted-foreground">No uploads yet. Drop your first invoice above.</p>
                        </div>
                    ) : (
                        <div className="divide-y">
                            {docs.map((d) => {
                                const si = statusInfo(d.status);
                                const Icon = si.icon;
                                const ex = d.extracted_data || {};
                                return (
                                    // Responsive row: stacks on mobile (identity -> badges -> actions), single row on sm+
                                    <div key={d.id} className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 px-5 py-3" data-testid={`inbox-doc-${d.id}`}>
                                        <div className="min-w-0">
                                            <p className="font-medium truncate flex items-center gap-2">
                                                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                                                {d.filename || 'Document'}
                                                <SourceBadge source={d.source} className="shrink-0" />
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-0.5 break-words">
                                                Uploaded {formatDate(d.created_at)}
                                                {ex.supplier_name ? ` · ${ex.supplier_name}` : ''}
                                                {ex.total_amount != null ? ` · ${formatCurrency(ex.total_amount, ex.currency)}` : ''}
                                            </p>
                                            <p className="text-xs mt-1">
                                                <PagesWarning doc={d} compact />
                                                {ex.invoice_date ? ` · ${ex.invoice_date}` : ''}
                                            </p>
                                        </div>
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 sm:shrink-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <Badge variant={d.doc_category === 'invoice' || !d.doc_category ? 'secondary' : 'outline'} className="whitespace-nowrap">
                                                    {d.doc_category === 'bank_statement' ? 'Bank statement' : d.doc_category === 'other' ? 'Document' : 'Invoice'}
                                                </Badge>
                                                <Badge variant={si.variant} className="whitespace-nowrap">
                                                    <Icon className="h-3 w-3 mr-1" /> {si.label}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button variant="ghost" size="sm" onClick={() => setViewDoc(d)} data-testid={`view-original-${d.id}`}>
                                                    <Eye className="h-4 w-4 mr-1" /> View
                                                </Button>
                                                {d.status !== 'approved' && (
                                                    <Button
                                                        variant="ghost" size="icon"
                                                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                                        onClick={() => setDocToDelete(d)} title="Delete this upload"
                                                        data-testid={`delete-doc-${d.id}`}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Original document viewer */}
            <Dialog open={!!viewDoc} onOpenChange={(o) => { if (!o) setViewDoc(null); }}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5" /> {viewDoc?.filename || 'Document'}
                        </DialogTitle>
                    </DialogHeader>
                    {viewDoc && (
                        <div className="space-y-3">
                            <p className="text-sm text-muted-foreground">
                                {(() => { const s = statusInfo(viewDoc.status); return s.label; })()}
                                {viewDoc.extracted_data?.supplier_name ? ` · ${viewDoc.extracted_data.supplier_name}` : ''}
                                {viewDoc.extracted_data?.total_amount != null ? ` · ${formatCurrency(viewDoc.extracted_data.total_amount, viewDoc.extracted_data.currency)}` : ''}
                                {viewDoc.extracted_data?.invoice_date ? ` · ${viewDoc.extracted_data.invoice_date}` : ''}
                            </p>
                            <BilagViewer docId={viewDoc.id} filename={viewDoc.filename}
                                          fileType={viewDoc.file_type} className="h-[70vh] border rounded-lg" />
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete upload confirmation */}
            <AlertDialog open={!!docToDelete} onOpenChange={(o) => { if (!o) setDocToDelete(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this upload?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {docToDelete && (<>This removes <span className="font-medium">{docToDelete.filename || 'this document'}</span> from your inbox and your accountant's queue. This cannot be undone. Approved/booked documents cannot be deleted.</>)}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={(e) => { e.preventDefault(); handleDeleteDoc(); }} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-doc">
                            {deleting ? 'Deleting…' : 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
